import React from "react";

import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InfoIcon from '@mui/icons-material/Info';
import InputAdornment from "@mui/material/InputAdornment";
import Box from "@mui/material/Box";
import { Divider } from "@mui/material";

export interface IDecorOption {
    id: string;
    name: string;
    icon?: string;
    tooltip?: string;
}

/**
 * Buta (prezentációs) decor-combobox. NINCS saját state-je, NINCS side effect-je:
 * mindent propból kap, minden változást onChange-en jelez. A sync / fallback logika
 * a Board- ill. EdgeDecorSelectorInput wrapperekben él, EGYMÁSTÓL FÜGGETLENÜL.
 * A megjelenítés (MUI Autocomplete + remount-fix + stílusok) az eredeti
 * DecorSelectorInput-ból VÁLTOZATLANUL átemelve.
 */
interface IDecorComboboxBaseProps {
    selectedOption: IDecorOption | null;
    /** Megjelenítendő (már rendezett) opciók. */
    options: IDecorOption[];
    /** Ha nem üres, ezek az ID-k csoportként kiemelve jelennek meg. */
    compatibleSet?: Set<string>;
    tooltip?: string;
    editable?: boolean;
    readOnly?: boolean;
    notFound?: boolean;
    /** Fókusz be/ki — a wrapperek ez alapján kapuzzák az auto-fallback-et szerkesztés közben. */
    onFocus?: () => void;
    onBlur?: () => void;
    onChange: (newId: string | null) => void;
}

const DecorComboboxBase: React.FC<IDecorComboboxBaseProps> = (props) => {
    const compatibleSet = props.compatibleSet;
    const hasCompatibleGroup = (compatibleSet?.size ?? 0) > 0;

    const getGroupKey = (option: IDecorOption) => {
        return compatibleSet?.has(option.id) ? "compatible" : "other";
    };

    return (
        <Tooltip title={props.tooltip || ""} arrow>
            {props.readOnly ?
                <Typography variant="subtitle1" component="div">
                    {props.selectedOption?.name || "N/A"}
                </Typography>
                :
                <Autocomplete
                    // Remount when the grouping mode toggles. MUI (>=7.3.7) keeps the
                    // previous option list mounted during the popup close animation
                    // (`renderedOptions = popupOpen ? groupedOptions : previousGroupedOptionsRef.current`),
                    // but reads `groupBy` fresh. If `groupBy` flips off->on while that
                    // stale list is still flat, MUI runs `option.options.map` on a
                    // non-group item and throws "Cannot read properties of undefined
                    // (reading 'map')". Forcing a fresh instance on toggle resets that
                    // ref and avoids the mismatch.
                    key={hasCompatibleGroup ? "grouped" : "flat"}
                    multiple={false}
                    onFocus={props.onFocus}
                    onBlur={props.onBlur}
                    // UX: fókuszra a teljes szöveg kijelölődik → gépelésre azonnal indul a
                    // keresés, nem kell karakterenként visszatörölni a kiválasztott nevet.
                    selectOnFocus
                    handleHomeEndKeys
                    size="small"
                    fullWidth
                    slotProps={{
                        listbox: {
                            sx: {
                                // Compatible element
                                '& .MuiAutocomplete-option[data-compatible]': {
                                    backgroundColor: '#bdbdbd',
                                },
                                // Selected (aria-selected)
                                '& .MuiAutocomplete-option[aria-selected="true"]': {
                                    backgroundColor: '#bdbdbd',
                                },
                                // Keyboard focus
                                '& .MuiAutocomplete-option.Mui-focused': {
                                    backgroundColor: '#e0e0e0 !important',
                                },
                                // Selected + focused
                                '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                                    backgroundColor: '#9e9e9e !important',
                                },
                                // Hover
                                '& .MuiAutocomplete-option:hover': {
                                    backgroundColor: '#e0e0e0 !important',
                                },
                            },
                        },
                    }}
                    options={props.options}
                    value={props.selectedOption}
                    disabled={props.editable === false || props.options.length === 0}
                    onChange={(e, newOption) => {
                        props.onChange(newOption ? newOption.id : null);
                    }}
                    getOptionLabel={(option) => option ? option.name : ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    {...(hasCompatibleGroup ? { groupBy: getGroupKey } : {})}
                    renderGroup={hasCompatibleGroup ? (params) => (
                        <Box key={params.key}>
                            {params.group === "other" && (
                                <Divider sx={{ my: 0.5 }} />
                            )}
                            {params.children}
                        </Box>
                    ) : undefined}
                    renderInput={(params) => <TextField
                        {...params}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                startAdornment: (props.notFound &&
                                    <Tooltip title="Ehhez az anyaghoz nincs kezdeti érték megadva" arrow>
                                        <InputAdornment position="start">
                                            <InfoIcon color="warning" />
                                        </InputAdornment>
                                    </Tooltip>
                                )
                            }
                        }}
                    />}
                    renderOption={(optionProps, option) => {
                        const isCompatible = compatibleSet?.has(option.id) ?? false;
                        const isSelected = option.id === props.selectedOption?.id;

                        return (
                            <li
                                {...optionProps}
                                key={option.id}
                                title={option.tooltip || undefined}
                                data-compatible={isCompatible || undefined}
                                data-current={isCompatible && isSelected || undefined}
                            >
                                {option.icon && (
                                    <span style={{ marginRight: '0.5em', display: 'inline-block', width: '1.2em', textAlign: 'center' }}>
                                        {option.icon}
                                    </span>
                                )}
                                {option.name}
                            </li>
                        );
                    }}
                />
            }
        </Tooltip>
    );
};

export default DecorComboboxBase;